// CalendarToolbar.tsx — Desktop-Top-Leiste des Kalender-Neubaus:
//   [+ Neuer Termin] │ [Tag][Woche][Monat] │ [⇄ Geteilte Ansicht] │ [▾ Filter] │ 🔍[…] │ [⚙][▾ Kalender]
//
// SP1 liefert: funktionsfähigen „+ Neuer Termin"-Button + ViewSwitch (schreibt
// in Context). SP5 befüllt die zuvor inaktiven Slots: Geteilte-Ansicht-Toggle
// (shared), Filter-Dropdown, Backend-SearchBox und Kalender-Dropdown.
// T-31: Zahnrad „Einstellungen für den Kalender" (Zeitskala) links neben dem
// Kalender-Dropdown; beide sitzen gemeinsam rechtsbündig.

import React from "react";
import { REFINED } from "../risikoanalyseStep/tokens";
import { CalendarView, useCalendarState } from "./CalendarStateContext";
import CalendarFilterDropdown from "./CalendarFilterDropdown";
import CalendarPicker from "./CalendarPicker";
import CalendarEinstellungen from "./CalendarEinstellungen";
import CalendarSearchBox from "./CalendarSearchBox";

interface CalendarToolbarProps {
    /** Klick auf „+ Neuer Termin". SP1: No-Op/Hinweis; SP2: openTerminFormWindow. */
    onNewTermin?: () => void;
}

const VIEW_LABELS: { value: CalendarView; label: string }[] = [
    { value: "day", label: "Tag" },
    { value: "week", label: "Woche" },
    { value: "month", label: "Monat" },
];

const CalendarToolbar: React.FC<CalendarToolbarProps> = ({ onNewTermin }) => {
    const { view, setView, shared, setShared, selectedCalendars } = useCalendarState();
    // Geteilte Ansicht ist nur sinnvoll mit >1 gewähltem Kalender.
    const sharedDisabled = selectedCalendars.length <= 1;

    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap",
                padding: "10px 14px",
                borderBottom: `1px solid ${REFINED.line}`,
                background: REFINED.white,
            }}
        >
            {/* + Neuer Termin (Primary) */}
            <button type="button" onClick={() => onNewTermin?.()} style={primaryBtnStyle}>
                + Neuer Termin
            </button>

            <span style={dividerStyle} aria-hidden />

            {/* ViewSwitch (Segmented) */}
            <div
                role="tablist"
                aria-label="Ansicht"
                style={{
                    display: "inline-flex",
                    border: `1px solid ${REFINED.line}`,
                    borderRadius: 8,
                    overflow: "hidden",
                    background: REFINED.white,
                }}
            >
                {VIEW_LABELS.map((v) => {
                    const active = view === v.value;
                    return (
                        <button
                            key={v.value}
                            type="button"
                            role="tab"
                            aria-selected={active}
                            onClick={() => setView(v.value)}
                            style={{
                                minHeight: 36,
                                padding: "0 16px",
                                border: "none",
                                background: active ? REFINED.aub : "transparent",
                                color: active ? REFINED.white : REFINED.ink,
                                font: `600 14px ${REFINED.fontUI}`,
                                cursor: "pointer",
                            }}
                        >
                            {v.label}
                        </button>
                    );
                })}
            </div>

            <span style={dividerStyle} aria-hidden />

            {/* ── SP5: Geteilte Ansicht / Filter / Suche / Kalender ── */}
            <button
                type="button"
                onClick={() => setShared(!shared)}
                disabled={sharedDisabled}
                aria-pressed={shared && !sharedDisabled}
                title={
                    sharedDisabled
                        ? "Mehrere Kalender wählen, um die geteilte Ansicht zu nutzen"
                        : "Geteilte Ansicht (Kalender nebeneinander)"
                }
                style={toggleBtnStyle(shared && !sharedDisabled, sharedDisabled)}
            >
                ⇄ Geteilte Ansicht
            </button>

            <CalendarFilterDropdown />
            <CalendarSearchBox />
            <div
                style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}
            >
                <CalendarEinstellungen />
                <CalendarPicker />
            </div>
        </div>
    );
};

const toggleBtnStyle = (active: boolean, disabled: boolean): React.CSSProperties => ({
    minHeight: 36,
    padding: "0 12px",
    borderRadius: 8,
    border: `1px solid ${active ? REFINED.aub : REFINED.line}`,
    background: active ? REFINED.aub : REFINED.white,
    color: active ? REFINED.white : disabled ? REFINED.ink3 : REFINED.ink,
    font: `600 14px ${REFINED.fontUI}`,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.6 : 1,
});

const primaryBtnStyle: React.CSSProperties = {
    minHeight: 36,
    padding: "0 16px",
    borderRadius: 8,
    border: "none",
    background: REFINED.aub,
    color: REFINED.white,
    font: `700 14px ${REFINED.fontUI}`,
    cursor: "pointer",
    boxShadow: REFINED.btnShadow,
};

const dividerStyle: React.CSSProperties = {
    width: 1,
    alignSelf: "stretch",
    minHeight: 24,
    background: REFINED.line,
};

export default CalendarToolbar;
